import React from "react";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";

/**
 * Higher-order component that adds a CONSISTENT execution indicator to ANY
 * workflow node, regardless of whether the node uses WorkflowBaseNode. It reads
 * `props.data.executionStatus` ("running" | "success" | "error") and overlays:
 *   - running → a centered spinner over a dimmed/blurred card (n8n-style)
 *   - success → a green check badge in the corner
 *   - error   → a red warning badge in the corner
 *
 * Wrapping every entry in the node registry guarantees the loader shows on every
 * node type from a single place.
 */
export function withNodeExecutionStatus<P extends { data?: any }>(
  Wrapped: React.ComponentType<P>,
): React.FC<P> {
  const Comp: React.FC<P> = (props) => {
    const status: string | null = props?.data?.executionStatus || null;
    const running = status === "running";
    const success = status === "success";
    const error = status === "error";

    return (
      <div className="relative w-fit">
        <Wrapped {...props} />

        {/* Running overlay — dim + center spinner */}
        {running && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-blue-500/10 backdrop-blur-[1px] pointer-events-none">
            <div className="bg-white rounded-full p-2.5 shadow-lg ring-1 ring-blue-100">
              <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
            </div>
          </div>
        )}

        {/* Success badge */}
        {success && (
          <div className="absolute -top-3 -right-3 z-30 pointer-events-none">
            <div className="bg-green-500 rounded-full p-1.5 border-2 border-white shadow">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          </div>
        )}

        {/* Error badge */}
        {error && (
          <div className="absolute -top-3 -right-3 z-30 pointer-events-none">
            <div className="bg-red-500 rounded-full p-1.5 border-2 border-white shadow">
              <AlertTriangle className="w-4 h-4 text-white" />
            </div>
          </div>
        )}
      </div>
    );
  };

  Comp.displayName = `withExec(${
    Wrapped.displayName || Wrapped.name || "Node"
  })`;
  return Comp;
}

export default withNodeExecutionStatus;
